<template>
  <div class="row page-wrapper">
    <div class="col-12">
      <div class="row justify-content-center page-header">
        <div class="col-12 col-xl-10">
          <h3>Neue Lizenz</h3>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-12 col-xl-10">
          <div class="row form-wrapper justify-content-center">
            <div class="col-12">
              <b-form
                class="new-entity-form"
                @submit="onSubmit"
              >
                <div class="row justify-content-start">
                  <div class="col-12 col-lg-6">
                    <b-form-group label="Lizenznehmer">
                      <vue-bootstrap-typeahead
                        :data="companies"
                        :serializer="s => s.name"
                        placeholder="Suche"
                        @hit="setSelectedCompany($event)"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-6">
                    <b-form-group label="Kontaktperson">
                      <b-form-input
                        :value="contactPerson"
                        :type="'text'"
                        disabled
                      />
                    </b-form-group>
                  </div>
                  <div class="col-12 col-lg-6">
                    <b-form-group label="Version">
                      <b-form-input
                        v-model="formData.version"
                        :type="'number'"
                        :max="maxVersion"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-6">
                    <b-form-group label="Geräteanzahl">
                      <b-form-input
                        v-model="formData.numberOfDevices"
                        :type="'number'"
                        :min="1"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-12 col-lg-6">
                    <b-form-group label="Gültig bis">
                      <datepicker
                        v-model="formData.validUntil"
                        :language="datepickerSettings.de"
                        :disabled-dates="datepickerSettings.disabledDates"
                        :format="'dd/MM/yyyy'"
                        :input-class="'datepicker bg-white'"
                        :bootstrap-styling="true"
                        :clear-button="true"
                        :monday-first="true"
                        type="date"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-6">
                    <b-form-group label="iPod">
                      <b-form-input
                        v-model="formData.iPodCount"
                        :type="'number'"
                        :min="0"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-12 col-lg-6">
                    <b-form-group label="iPad">
                      <b-form-input
                        v-model="formData.iPadCount"
                        :type="'number'"
                        :min="0"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <b-form-group label="Modules">
                      <b-form-checkbox-group
                        v-model="formData.modules"
                        :options="licenseModuleOptions"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="row justify-content-end">
                  <div class="col-12">
                    <b-form-group class="form-button-group">
                      <b-button
                        variant="outline-primary"
                        class="btn-cancel"
                        @click="goBack"
                      >
                        Zurück
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                        :disabled="!isFormValid"
                      >
                        Erstellen
                      </b-button>
                    </b-form-group>
                  </div>
                </div>
              </b-form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {de} from 'vuejs-datepicker/dist/locale';
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';
import Datepicker from 'vuejs-datepicker';
import {NavigationMixin, LicenseMixin, CompanyMixin, AuthMixin} from '../../mixins';
import appConfig from '../../app.config';
import {LICENSE_MODULES, ROLE_AVAILABLE_LICENSE_MODULES} from '../../constants';
import {BFormCheckboxGroup} from 'bootstrap-vue';

export default {
  name: 'AddLicense',
  components: {
    VueBootstrapTypeahead,
    Datepicker,
    BFormCheckboxGroup
  },
  mixins: [NavigationMixin, LicenseMixin, CompanyMixin, AuthMixin],
  data: function () {
    return {
      formData: {
        companyId: null,
        numberOfDevices: null,
        iPodCount: 0,
        iPadCount: 0,
        validUntil: null,
        version: appConfig.defaultLicenseVersion,
        modules: []
      },
      companies: [],
      selectedCompany: null,
      datepickerSettings: {
        de,
        disabledDates: {
          to: new Date()
        }
      },
      maxVersion: appConfig.defaultLicenseVersion,
      licenseModuleOptions: []
    };
  },
  computed: {
    contactPerson() {
      return this.selectedCompany ? `${this.selectedCompany.contactPersonName || ''} ${this.selectedCompany.email || ''}` : '';
    },
    isFormValid() {
      return this.formData.companyId && this.formData.numberOfDevices && this.formData.validUntil && this.formData.version !== null;
    }
  },
  created() {
    this.licenseModuleOptions = Object.values(LICENSE_MODULES).filter(m => ROLE_AVAILABLE_LICENSE_MODULES[this.auth.role].includes(m));
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      const response = await this.companyService.getCompaniesLookup();
      this.companies = response.companies;
    },
    async onSubmit(evt) {
      evt.preventDefault();
      if (this.isFormValid) {
        try {
          const payload = {...this.formData};
          const validUntil = moment.utc(payload.validUntil);
          validUntil.set('hour', 0);
          validUntil.set('minute', 0);
          validUntil.set('second', 0);
          validUntil.set('millisecond', 0);
          payload.validUntil = validUntil.toISOString();
          await this.licenseService.createLicense(payload);
          this.goToPath('/licenses');
        } catch (err) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Neue Lizenz',
            text: (err && err.error) ? err.error.message : 'Ooops!'
          });
        }
      }
    },
    setSelectedCompany(company) {
      this.selectedCompany = company;
      this.formData.companyId = company.id;
    }
  }
};
</script>
